import { useContext } from 'react'
import { StoreContext } from '@/providers/storeProvider'
import { MarketCode } from '@/network/graphql.g'
import { DateTime, Interval } from 'luxon'
import { useRouter } from 'next/router'
import { Routes } from '@/core/routes'

type AssetFunction = (path: Asset, includeFilatic?: boolean) => string

type UseAsset = {
  asset: AssetFunction
  isCountryForDogSmileFace: boolean
  isRsAndBaMarket: boolean
  isCartProcessStep: boolean
  hidePaymentIcons: boolean
  useSOIPricing: boolean
  isNewSearch: boolean
  showHeureka: boolean
  isInfoPopUpPromoTime: boolean
  showReviews: boolean
  useSpeedCurveOpt: boolean
  isMyOrderCountry: boolean
  hidePhoneContact: boolean
  showRevolutIcon: boolean
}

const getAsset =
  (domainName: string, isFilatic: boolean): AssetFunction =>
  (path, includeFilatic = false) =>
    path
      ? `/${includeFilatic && isFilatic ? 'filatic' : domainName}/${path}`
      : ''

export const useAsset = (): UseAsset => {
  const {
    subdomainName,
    domainName,
    isFilatic,
    isFactcool,
    isFrogies,
    isBezvasport
  } = useContext(StoreContext)

  const isCountryForDogSmileFace =
    isFactcool &&
    [
      MarketCode.Sk,
      MarketCode.Cs,
      MarketCode.Hr,
      MarketCode.Gr,
      MarketCode.Bg
    ].includes(subdomainName as MarketCode)

  const isRsAndBaMarket = [MarketCode.Ba, MarketCode.Rs].includes(
    subdomainName as MarketCode
  )

  const hidePaymentIcons = [MarketCode.Rs, MarketCode.Ba].includes(
    subdomainName as MarketCode
  )

  const hidePhoneContact = [MarketCode.Gr].includes(subdomainName as MarketCode)

  const router = useRouter()

  const isCartProcessStep =
    router.route === Routes.cart ||
    router.route === Routes.deliveryCost ||
    router.route === Routes.deliveryAddress

  const useBezvasportCSPricing = isBezvasport && subdomainName === MarketCode.Cs

  const isSOIPricingMarket = [MarketCode.Bg].includes(
    subdomainName as MarketCode
  )

  const useSOIPricing = useBezvasportCSPricing || isSOIPricingMarket

  const isNewSearch = !isFrogies

  const showHeureka =
    isBezvasport && [MarketCode.Cs].includes(subdomainName as MarketCode)

  const currentDate = DateTime.now()
  const startDateInfoPopUp = DateTime.local(2025, 5, 7, 0, 1)
  const endDateInfoPopUp = DateTime.local(2026, 12, 31, 23, 59)
  const isInfoPopUpPromoTime = Interval.fromDateTimes(
    startDateInfoPopUp,
    endDateInfoPopUp
  ).contains(currentDate)

  const showReviews =
    isFactcool && [MarketCode.Sk].includes(subdomainName as MarketCode)

  const useSpeedCurveOpt =
    isFactcool && [MarketCode.Sk].includes(subdomainName as MarketCode)

  const showRevolutIcon = [
    MarketCode.Cs,
    MarketCode.Bg,
    MarketCode.Hr,
    MarketCode.Hu,
    MarketCode.Ro,
    MarketCode.Si,
    MarketCode.Sk,
    MarketCode.Pl,
    MarketCode.Gr
  ].includes(subdomainName as MarketCode)

  const isMyOrderCountry = [
    MarketCode.Sk,
    MarketCode.Hu,
    MarketCode.Cs,
    MarketCode.Ro,
    MarketCode.Bg,
    MarketCode.Hr,
    MarketCode.Si,
    MarketCode.Pl,
    MarketCode.Gr,
    MarketCode.En
  ].includes(subdomainName as MarketCode)

  return {
    asset: getAsset(domainName, isFilatic),
    isCountryForDogSmileFace,
    isRsAndBaMarket,
    isCartProcessStep,
    hidePaymentIcons,
    useSOIPricing,
    isNewSearch,
    showHeureka,
    isInfoPopUpPromoTime,
    showReviews,
    useSpeedCurveOpt,
    isMyOrderCountry,
    showRevolutIcon,
    hidePhoneContact
  }
}

export enum Asset {
  Logo = 'logo.webp',
  ErrorLogo = 'error-logo.webp',
  InvertedLogo = 'inverted-logo.webp',
  MobileMenuLogo = 'mobile-menu-logo.webp',
  BGDropdown = 'bg-dropdown.webp',
  Insurance = 'insurance.png',
  Packatory = 'packatory.png',
  Favicon = 'favicon.png',
  BGNotFound = 'bg-lines-dark.webp',
  toolbarBackground = 'toolbar-background.webp',
  SadDog = 'sad-dog.webp',
  HappyDog = 'happy-dog.webp'
}
